import { z } from 'zod';
import { CANDIDATE_FIELDS } from './candidates.js';
/**
 * Entity kinds with an overview layout. Its own list, not the custom-field one, so a kind can
 * carry custom fields before any page reads a layout for it. Companies and projects joined in
 * TS-1752, contacts, jobs and deals in TS-1840: their layouts place custom fields only (see
 * `LAYOUT_STANDARD_FIELDS`).
 */
export const LAYOUT_ENTITIES = [
    'candidates',
    'companies',
    'contacts',
    'projects',
    'deals',
    'jobs',
];
export const layoutEntitySchema = z.enum(LAYOUT_ENTITIES);
/**
 * Overview layout (RELAY-944): how a record's detail page groups its fields. One layout per
 * organisation and entity kind, edited by an admin in Settings → Fields; every detail page
 * reads it. A field is a standard column name or `custom.<key>`. Fields a layout does not
 * mention are not hidden: the page appends them in a trailing group (custom fields under
 * their definition's group), so a newly created field shows up without touching the layout.
 */
export const layoutGroupSchema = z.object({
    name: z.string().trim().min(1).max(60),
    fields: z.array(z.string().min(1).max(100)).max(100),
});
export const overviewLayoutSchema = z
    .object({ groups: z.array(layoutGroupSchema).max(30) })
    .superRefine((layout, ctx) => {
    const seen = new Set();
    layout.groups.forEach((g, gi) => {
        g.fields.forEach((f, fi) => {
            if (seen.has(f))
                ctx.addIssue({
                    code: 'custom',
                    path: ['groups', gi, 'fields', fi],
                    message: `'${f}' is placed twice`,
                });
            seen.add(f);
        });
    });
});
export const layoutInputSchema = z.object({
    entity: layoutEntitySchema,
    layout: overviewLayoutSchema,
});
/** Standard candidate fields the detail page can place; the derived/system ones are left out. */
export const CANDIDATE_LAYOUT_FIELDS = Object.keys(CANDIDATE_FIELDS).filter((f) => !['id', 'primaryEmail', 'primaryPhone', 'createdAt', 'updatedAt'].includes(f));
/** Where a candidate's fields go when an organisation has not arranged them yet. */
export const DEFAULT_CANDIDATE_LAYOUT = {
    groups: [
        { name: 'Contact', fields: ['email', 'phone', 'altPhone', 'country', 'region'] },
        {
            name: 'Profile',
            fields: [
                'currentRole',
                'currentCompany',
                'yearsExperience',
                'educationLevel',
                'languages',
                'workType',
                'expectedHourlyRate',
                'dateOfBirth',
                'gender',
            ],
        },
        {
            name: 'Hiring',
            fields: [
                'stage',
                'status',
                'source',
                'ownerId',
                'applicationDate',
                'interviewDate',
                'onboardingDate',
                'isBlocked',
                'crmDisqualified',
            ],
        },
        {
            name: 'External',
            fields: ['recruitCrmId', 'recruitCrmSlug', 'relayResumeId', 'resumeFilename'],
        },
    ],
};
/**
 * Standard fields each kind's layout may place, besides `custom.<key>`. Every kind but
 * candidates has none on purpose (TS-1752, TS-1840): their pages draw their standard fields
 * themselves (stage, company, owner, description) and the layout arranges the custom fields.
 */
export const LAYOUT_STANDARD_FIELDS = {
    candidates: CANDIDATE_LAYOUT_FIELDS,
    companies: [],
    contacts: [],
    projects: [],
    deals: [],
    jobs: [],
};
/**
 * The layout an organisation gets until it stores one. An empty one for every kind but
 * candidates: every custom field is then appended under its definition's group, which is the
 * grouping an admin set on the field.
 */
export const DEFAULT_LAYOUTS = {
    candidates: DEFAULT_CANDIDATE_LAYOUT,
    companies: { groups: [] },
    contacts: { groups: [] },
    projects: { groups: [] },
    deals: { groups: [] },
    jobs: { groups: [] },
};
/** Human labels for the standard candidate fields, shared by the detail page and the layout editor. */
export const CANDIDATE_FIELD_LABELS = {
    firstName: 'First name',
    lastName: 'Last name',
    email: 'Email',
    phone: 'Phone',
    altPhone: 'Alternate phone',
    dateOfBirth: 'Date of birth',
    gender: 'Gender',
    currentRole: 'Current role',
    currentCompany: 'Current company',
    yearsExperience: 'Years of experience',
    educationLevel: 'Education level',
    workType: 'Work type',
    region: 'Region',
    country: 'Country',
    languages: 'Languages',
    expectedHourlyRate: 'Expected hourly rate (USD)',
    stage: 'Stage',
    status: 'Status',
    source: 'Source',
    ownerId: 'Owner',
    applicationDate: 'Application date',
    interviewDate: 'Interview date',
    onboardingDate: 'Onboarding date',
    isBlocked: 'Blocked',
    crmDisqualified: 'Disqualified in CRM',
    resumeFilename: 'Legacy resume filename',
    recruitCrmId: 'RecruitCRM id',
    recruitCrmSlug: 'RecruitCRM slug',
    relayResumeId: 'Relay resume id',
};
//# sourceMappingURL=layouts.js.map